import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '@/components/dialogs/Modal';
import { Select2 } from '@/components/forms/Select2';
import { moneyTransactionsApi } from '@/api/services/moneyTransactions';

interface Props {
  show: boolean;
  // The current provider title — disabled in the dropdown (can't reassign to self).
  currentProviderTitle: string;
  selectedCount: number;
  busy?: boolean;
  onConfirm: (provider: string, comment: string) => void;
  onCancel: () => void;
}

// Bulk Edit-Provider modal (ps-edit-provider.js): a provider select2 (current provider
// disabled) + mandatory comment, then a second "Are you sure?" confirm before submit.
export function BulkEditProviderDialog({
  show,
  currentProviderTitle,
  selectedCount,
  busy = false,
  onConfirm,
  onCancel,
}: Props) {
  const [provider, setProvider] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [touched, setTouched] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const options = useQuery({
    queryKey: ['money-transactions', 'provider-options'],
    queryFn: moneyTransactionsApi.getProviderOptions,
    enabled: show,
  });

  const providerInvalid = !provider;
  const commentInvalid = comment.trim().length === 0;

  const select2Options = (options.data ?? []).map((o) => ({
    value: String(o.value),
    label: o.label,
    disabled: String(o.value) === currentProviderTitle,
  }));

  function reset() {
    setProvider(null);
    setComment('');
    setTouched(false);
    setConfirming(false);
  }

  function handleSubmit() {
    if (providerInvalid || commentInvalid) {
      setTouched(true);
      return;
    }
    setConfirming(true);
  }

  function handleConfirm() {
    onConfirm(provider as string, comment.trim());
    reset();
  }

  function handleCancel() {
    reset();
    onCancel();
  }

  if (confirming) {
    return (
      <Modal
        show={show}
        title="Are you sure?"
        onClose={handleCancel}
        footer={
          <>
            <button type="button" className="btn btn-light" onClick={() => setConfirming(false)} disabled={busy}>
              No
            </button>
            <button type="button" className="btn btn-primary" onClick={handleConfirm} disabled={busy}>
              {busy ? (
                <>
                  <i className="fa fa-spinner fa-spin me-1" />
                  Working…
                </>
              ) : (
                'Yes'
              )}
            </button>
          </>
        }
      >
        <p>This action will update the provider of the checked transactions.</p>
      </Modal>
    );
  }

  return (
    <Modal
      show={show}
      title="Select a new provider and enter a comment to proceed with the change."
      onClose={handleCancel}
      footer={
        <>
          <button type="button" className="btn btn-light" onClick={handleCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={busy}>
            Submit
          </button>
        </>
      }
    >
      <p className="small text-muted">{selectedCount} transaction(s) selected.</p>
      <div className="mb-3">
        <label className="form-label">Provider</label>
        <Select2
          value={provider}
          onChange={setProvider}
          options={select2Options}
          placeholder={options.isLoading ? 'Loading…' : 'Select a provider'}
          allowClear={false}
          error={touched && providerInvalid ? 'Please select a provider.' : undefined}
        />
      </div>
      <label className="form-label mb-2">Add comment below</label>
      <textarea
        className={`form-control${touched && commentInvalid ? ' is-invalid' : ''}`}
        rows={3}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
      />
      {touched && commentInvalid && <div className="invalid-feedback d-block">Please add a comment.</div>}
    </Modal>
  );
}
